// @noflow
/*eslint react/no-did-mount-set-state:0*/

/**
 * The main logic of this component is around the 2 state variables displayFloatIndex
 * and targetFloatIndex. The range of the indexes is from 0 to props.rangeValues.lengt - 1.
 * Both indexes are float numbers for animation purpose. the displayFloatIndex state
 * represent the current position of the spinner and the targert one represent the desired
 * position (e.g.: key up)
 */

import { debounce } from 'lodash/fp';
import Hamster from 'hamsterjs';
import PropTypes from 'prop-types';
import * as React from 'react';
import SpinnerValueComponent from './spinner-value/spinner-value.component';

// define global constant linked to the platform type
const isMac = window.navigator.platform === 'MacIntel';
let PIXEL_RATIO = window.devicePixelRatio || 1;
if (isMac) {
    PIXEL_RATIO <<= 1;
}
const updateDelay = 750;

/************************
  *   Helper functions   *
  ************************/

const _getAbsoluteIndex = (index, length) => (length ? (index % length + length) % length : 0);
const _getDisplayedValue = (rangeValues, index) => {
    if (typeof rangeValues[index] === 'string') {
        return rangeValues[index];
    } else {
        return rangeValues[index] ? rangeValues[index][0].text : '';
    }
};

/*****************
  *   Component   *
  *****************/
export default class Spinner extends React.Component {
    static propTypes = {
        bgColor: PropTypes.object,
        color: PropTypes.object,
        currentIndex: PropTypes.number,
        font: PropTypes.string,
        handleChangeIndex: PropTypes.func,
        isLoop: PropTypes.bool,
        rangeColors: PropTypes.array,
        rangeValues: PropTypes.array,
        style: PropTypes.object
    };

    static defaultProps = {
        isLoop: false,
        rangeValues: []
    };

    constructor(props) {
        super(props);

        this.state = {
            ctx: null,
            currentIndex: 0,
            displayFloatIndex: props.currentIndex || 0,
            pointerId: null,
            pointerMoving: false,
            targetFloatIndex: props.currentIndex || 0
        };
    }

    componentDidMount() {
        const ctx = this.canvas.getContext('2d');

        ctx.textBaseline = 'alphabetic';

        if (window.navigator.pointerEnabled || window.navigator.msPointerEnabled) {
            this.canvas.addEventListener('pointerdown', this.handlePointerDown);
            window.addEventListener('pointermove', this.handlePointerMove);
            window.addEventListener('pointerup', this.handlePointerUp);
        } else if ('ontouchstart' in window) {
            this.canvas.addEventListener('touchstart', this.handleTouchStart);
            this.canvas.addEventListener('touchmove', this.handleTouchMove);
            window.addEventListener('touchend', this.handleTouchEnd);
        }

        Hamster(this.canvas).wheel(this.handleMouseWheel);

        window.addEventListener('resize', this.handleResize);
        const { container } = this;

        if (container) {
            const check = () =>
                container.offsetHeight > 0 && container.offsetWidth > 0
                    ? this.setState({
                          ctx,
                          height: container.offsetHeight,
                          width: container.offsetWidth
                      })
                    : setTimeout(check, 100);
            check();
        }
    }

    componentWillUnmount() {
        this.canvas.removeEventListener('touchstart', this.handleTouchStart);
        this.canvas.removeEventListener('touchmove', this.handleTouchMove);
        window.removeEventListener('touchend', this.handleTouchEnd);

        this.canvas.removeEventListener('pointerdown', this.handlePointerDown);
        window.removeEventListener('pointermove', this.handlePointerMove);
        window.removeEventListener('pointerup', this.handlePointerUp);

        window.removeEventListener('resize', this.handleResize);
    }

    // if no update in the last 750ms then let the parent component know the current index has changed
    debouncedChange = debounce(
        updateDelay,
        ({ handleChangeIndex, roundedTargetIndex, rangeValues }) =>
            handleChangeIndex && handleChangeIndex(_getAbsoluteIndex(roundedTargetIndex, rangeValues.length))
    );

    componentWillUpdate(nextProps, nextState) {
        const { currentIndex, handleChangeIndex, isLoop, rangeValues } = this.props;
        const { targetFloatIndex } = this.state;

        if (
            currentIndex !== nextProps.currentIndex &&
            (!isLoop || (nextProps.currentIndex - targetFloatIndex) % rangeValues.length)
        ) {
            this.setState({
                animationId: window.requestAnimationFrame(this.animate),
                targetFloatIndex: nextProps.currentIndex
            });
        } else {
            const roundedTargetIndex = Math.round(nextState.targetFloatIndex);

            if (
                targetFloatIndex !== nextState.targetFloatIndex &&
                roundedTargetIndex !== nextProps.currentIndex &&
                handleChangeIndex
            ) {
                this.debouncedChange({
                    handleChangeIndex,
                    roundedTargetIndex,
                    rangeValues
                });
            }
        }
    }

    /**
	 * The aim of the animate function is to aproximate the display index
	 * to the target one by 10% of the diference in each frame until it
	 * gets so close then they become equal
	 */
    animate = () => {
        const { animationId, displayFloatIndex, targetFloatIndex } = this.state;
        const delta = (targetFloatIndex - displayFloatIndex) / 10;
        if (Math.abs(delta) >= 0.001) {
            this.setState({
                animationId: window.requestAnimationFrame(this.animate),
                displayFloatIndex: displayFloatIndex + delta
            });
        } else {
            window.cancelAnimationFrame(animationId);
            this.setState({
                displayFloatIndex: targetFloatIndex
            });
        }
    };

    /**
	 * Round the target index (e.g. 2.23 -> 2, 3.834 -> 4)
	 */
    snapValue = () => {
        const { targetFloatIndex } = this.state;

        if (targetFloatIndex % 1) {
            this.setState({
                animationId: window.requestAnimationFrame(this.animate),
                targetFloatIndex: Math.round(targetFloatIndex)
            });
        }
    };

    /************************
	 *   ACTION FUNCTIONS   *
	 ************************/

    up = () => {
        this.setState((prevState, { isLoop }) => {
            if (isLoop || prevState.targetFloatIndex > 0) {
                return {
                    animationId: window.requestAnimationFrame(this.animate),
                    pointerId: null,
                    pointerMoving: false,
                    targetFloatIndex: Math.round(prevState.targetFloatIndex - 1)
                };
            }
        });
    };

    down = () => {
        this.setState((prevState, { isLoop, rangeValues }) => {
            if (isLoop || prevState.targetFloatIndex < rangeValues.length - 1) {
                return {
                    animationId: window.requestAnimationFrame(this.animate),
                    pointerId: null,
                    pointerMoving: false,
                    targetFloatIndex: Math.round(prevState.targetFloatIndex + 1)
                };
            }
        });
    };

    click = event => {
        const { height } = this.state;

        const bound = this.canvas.getBoundingClientRect();
        const y = event.clientY - bound.top;
        if (y <= height || y > 0) {
            if (y <= height / 2) {
                this.up();
            } else {
                this.down();
            }
        }
    };

    /*** POINTER ***/

    pointerDown = (event, pointerId) => {
        this.setState({
            delta: 0,
            pointerDownY: event.screenY,
            pointerId: pointerId,
            pointerMoving: false
        });
    };

    pointerMove = pointer => {
        const { isLoop, rangeValues } = this.props;
        const { displayFloatIndex, pointerDownY, pointerMoving } = this.state;

        const newPointerY = pointer.screenY;
        const delta = newPointerY - pointerDownY;
        let newDisplayFloatIndex = displayFloatIndex - delta * 0.01;
        if (!isLoop) {
            newDisplayFloatIndex = Math.max(newDisplayFloatIndex, -0.49);
            newDisplayFloatIndex = Math.min(newDisplayFloatIndex, rangeValues.length - 1 + 0.49);
        }
        this.setState({
            delta,
            displayFloatIndex: newDisplayFloatIndex,
            pointerDownY: newPointerY,
            pointerMoving: pointerMoving || newPointerY !== pointerDownY
        });
    };

    pointerUp = () => {
        const { isLoop, rangeValues } = this.props;
        const { delta, displayFloatIndex } = this.state;

        let newTargetFloatIndex = Math.round(displayFloatIndex - delta * 0.2);
        if (!isLoop) {
            newTargetFloatIndex = Math.max(newTargetFloatIndex, 0);
            newTargetFloatIndex = Math.min(newTargetFloatIndex, rangeValues.length - 1);
        }
        this.setState({
            animationId: window.requestAnimationFrame(this.animate),
            pointerId: null,
            targetFloatIndex: newTargetFloatIndex
        });
    };

    /************************
	 *   EVENTS FUNCTIONS   *
	 ************************/

    handleClick = event => {
        const { pointerMoving } = this.state;

        if (this.props.disabled) {
            return;
        }

        if (pointerMoving) {
            this.setState({
                pointerMoving: false
            });
        } else {
            this.click(event);
        }
    };

    /*** Mouse wheel and trackpad scrolling ***/

    handleMouseWheel = (event, delta, deltaX, deltaY) => {
        const { isLoop, rangeValues } = this.props;
        const { displayFloatIndex, snapTimeOut, animationId } = this.state;

        event.preventDefault();

        if (this.props.disabled) {
            return;
        }

        clearTimeout(snapTimeOut);

        if (isNaN(deltaY)) {
            deltaY = 0;
        }

        if (!isMac) {
            deltaY *= 20;
        }

        window.cancelAnimationFrame(animationId);

        let newDisplayFloatIndex = displayFloatIndex - Math.sign(deltaY) * Math.min(Math.abs(deltaY * 0.1), 2);
        if (!isLoop) {
            newDisplayFloatIndex = Math.max(newDisplayFloatIndex, 0);
            newDisplayFloatIndex = Math.min(newDisplayFloatIndex, rangeValues.length - 1);
        }

        // if no event was called in the last 200ms then round the target index
        const newTimeOut = setTimeout(() => {
            this.snapValue();
        }, 200);

        this.setState({
            animationId: window.requestAnimationFrame(this.animate),
            targetFloatIndex: newDisplayFloatIndex,
            snapTimeOut: newTimeOut
        });
    };

    /*** Touch events ***/

    handleTouchStart = event => {
        const { pointerId } = this.state;

        if (this.props.disabled) {
            return;
        }

        if (pointerId === null) {
            event.preventDefault();
            const touch = event.targetTouches[0];
            this.pointerDown(touch, touch.identifier);
        }
    };

    handleTouchMove = event => {
        const { pointerId } = this.state;

        if (this.props.disabled) {
            return;
        }

        const touch = Object.keys(event.changedTouches)
            .map(key => event.changedTouches[key])
            .find(touch => touch.identifier === pointerId);

        if (touch) {
            event.preventDefault();
            this.pointerMove(touch);
        }
    };

    handleTouchEnd = event => {
        const { pointerId, pointerMoving } = this.state;

        if (this.props.disabled) {
            return;
        }

        const touch = Object.keys(event.changedTouches)
            .map(key => event.changedTouches[key])
            .find(touch => touch.identifier === pointerId);

        if (touch) {
            event.preventDefault();
            if (pointerMoving) {
                this.pointerUp();
            } else {
                this.click(touch);
            }
        }
    };

    /*** Internet Explorer pointer ***/

    handlePointerDown = event => {
        event.preventDefault();
        const { pointerId } = this.state;

        if (this.props.disabled) {
            return;
        }

        if (event.pointerType !== 'mouse' && pointerId === null) {
            this.pointerDown(event, event.pointerId);
        }
    };

    handlePointerMove = event => {
        event.preventDefault();
        const { pointerId } = this.state;

        if (this.props.disabled) {
            return;
        }

        if (event.pointerType !== 'mouse' && pointerId === event.pointerId) {
            this.pointerMove(event);
        }
    };

    handlePointerUp = event => {
        event.preventDefault();
        const { pointerId } = this.state;

        if (this.props.disabled) {
            return;
        }

        if (event.pointerType !== 'mouse' && pointerId === event.pointerId) {
            this.pointerUp();
        }
    };

    /*** Key events ***/

    handleKeyDown = event => {
        if (this.props.disabled) {
            return;
        }
        if (event.keyCode === 38) {
            this.up();
        } else if (event.keyCode === 40) {
            this.down();
        }
    };

    handleResize = () => {
        const { container } = this;

        if (container) {
            this.setState({
                height: container.offsetHeight,
                width: container.offsetWidth
            });
        }
    };

    render() {
        const { bgColor, color, font, isLoop, rangeColors, rangeValues, style, disabled } = this.props;
        const { ctx, displayFloatIndex, height, width } = this.state;

        return (
            <div
                data-qa="spinner"
                onKeyDown={this.handleKeyDown}
                ref={r => (this.container = r)}
                style={style}
                tabIndex="0"
            >
                <canvas
                    aria-label="Spinner"
                    className="spinner"
                    data-qa={`spinner-value-${_getDisplayedValue(rangeValues, displayFloatIndex)}`}
                    data-qa-max={`spinner-value-${_getDisplayedValue(rangeValues, 0)}`}
                    data-qa-min={`spinner-value-${_getDisplayedValue(rangeValues, rangeValues.length - 1)}`}
                    height={height * PIXEL_RATIO}
                    onClick={this.handleClick}
                    ref={r => (this.canvas = r)}
                    style={{
                        ...style
                    }}
                    width={width * PIXEL_RATIO}
                />
                <SpinnerValueComponent
                    bgColor={bgColor}
                    color={color}
                    ctx={ctx}
                    disabled={disabled}
                    displayFloatIndex={
                        isLoop ? _getAbsoluteIndex(displayFloatIndex, rangeValues.length) : displayFloatIndex
                    }
                    font={font}
                    height={height * PIXEL_RATIO}
                    isLoop={isLoop}
                    rangeColors={rangeColors}
                    rangeValues={rangeValues}
                    width={width * PIXEL_RATIO}
                />
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/widgets/spinner/spinner.component.js